import { useMemo } from "react";
import { GlobalInventory } from "../State/InventoryContext";
import { MINER_BEHAVIORS, SMELTER_RECIPES } from "../State/GameData";
import { AssemblySetup } from "../Assembly/AssemblyTypes";
import { ASSEMBLY_RECIPES } from "../State/AssemblyRecipes";

/**
 * Data structure for resource inventory statistics
 */
export interface ResourceStat {
    name: string;
    value: number;
    itemId: string;
}

/**
 * Data structure for production statistics (mining/smelting)
 */
export interface ProductionStat {
    total: number;
    running: number;
}

/**
 * Data structure for power generation statistics
 */
export interface PowerStats {
    current: number;
    max: number;
    demand: number;
}

/**
 * Data structure for assembly item flow tracking
 */
export interface AssemblyItemFlow {
    consumed: number;
    produced: number;
    destinations: string[];
}

/**
 * Complete dashboard statistics
 */
export interface DashboardStats {
    resources: ResourceStat[];
    basicMaterials: ResourceStat[];
    researchCount: number;
    miningStats: Record<string, ProductionStat>;
    totalMinedPerSecond: number;
    smeltingStats: Record<string, ProductionStat>;
    totalSmeltedPerSecond: number;
    totalCrafted: number;
    power: PowerStats;
    assemblerCount: number;
    runningAssemblers: number;
    assemblyItemFlow: Record<string, AssemblyItemFlow>;
    assemblerRecipes: Record<string, number>;
}

/**
 * Custom hook to calculate all dashboard statistics from the global inventory
 *
 * This hook performs comprehensive calculations including:
 * - Resource and material inventory totals
 * - Mining and smelting production rates
 * - Power generation and consumption
 * - Assembly line statistics and item flow analysis
 *
 * @param inventory - The global inventory state
 * @returns Calculated dashboard statistics
 */
export function useDashboardStats(inventory: GlobalInventory): DashboardStats {
    return useMemo(() => {
        // Resources in inventory (for pie chart)
        const resources = inventory.inventory
            .filter(item => item.item.category === "resource" && item.quantity > 0)
            .map(item => ({
                name: item.item.name,
                value: item.quantity,
                itemId: item.item.id,
            }));

        // Basic materials (for histogram)
        const basicMaterials = inventory.inventory
            .filter(item => item.item.category === "basicMaterial" && item.quantity > 0)
            .map(item => ({
                name: item.item.name,
                value: item.quantity,
                itemId: item.item.id,
            }));

        // Research count
        const researchCount = inventory.technologies?.length ?? 0;

        // Mining stats per second
        const miningStats: Record<string, ProductionStat> = {};
        inventory.minerState.forEach(field => {
            const runningMiners = field.miners.filter(m => m.running);
            const behavior = MINER_BEHAVIORS[runningMiners[0]?.itemId];

            if (behavior && runningMiners.length > 0) {
                const cycleTimeSeconds = behavior.cycleTimeMs / 1000;
                const perSecond = runningMiners.length * (behavior.outputPerCycle / cycleTimeSeconds);

                miningStats[field.resourceType] = {
                    total: perSecond,
                    running: runningMiners.length,
                };
            }
        });

        const totalMinedPerSecond = Object.values(miningStats).reduce((sum, stat) => sum + stat.total, 0);

        // Smelting stats per second
        const smeltingStats: Record<string, ProductionStat> = {};
        inventory.smelterState.forEach(field => {
            const runningSmelters = field.smelters.filter(s => s.running);
            const recipe = SMELTER_RECIPES[field.resourceType];

            if (recipe && runningSmelters.length > 0) {
                const cycleTimeSeconds = recipe.cycleTimeMs / 1000;
                const perSecond = runningSmelters.length * (recipe.outputQuantity / cycleTimeSeconds);

                smeltingStats[field.resourceType] = {
                    total: perSecond,
                    running: runningSmelters.length,
                };
            }
        });

        const totalSmeltedPerSecond = Object.values(smeltingStats).reduce((sum, stat) => sum + stat.total, 0);

        // Total items crafted (inventory + placed)
        let totalCrafted = 0;

        // Count items in inventory
        inventory.inventory.forEach(item => {
            if (item.item.category !== "resource") {
                totalCrafted += item.quantity;
            }
        });

        // Count placed miners
        inventory.minerState.forEach(field => {
            totalCrafted += field.miners.length;
        });

        // Count placed smelters
        inventory.smelterState.forEach(field => {
            totalCrafted += field.smelters.length;
        });

        // Count power machines
        totalCrafted += inventory.powerMachines.waterPumps.length;
        totalCrafted += inventory.powerMachines.waterBoilers.length;
        totalCrafted += inventory.powerMachines.steamGenerators.length;

        // Assembly stats
        let assemblerCount = 0;
        let runningAssemblers = 0;
        const assemblyItemFlow: Record<string, AssemblyItemFlow> = {};
        const assemblerRecipes: Record<string, number> = {};

        try {
            const stored = localStorage.getItem("assembly-setups");
            if (stored) {
                const setups: AssemblySetup[] = JSON.parse(stored);
                setups.forEach(setup => {
                    setup.nodes.forEach(node => {
                        if (node.type === "assembler") {
                            assemblerCount++;
                            if (node.running) {
                                runningAssemblers++;
                            }

                            // Track what recipe this assembler is using
                            if (node.recipeId) {
                                assemblerRecipes[node.recipeId] = (assemblerRecipes[node.recipeId] || 0) + 1;

                                // Track ingredient consumption
                                const recipe = ASSEMBLY_RECIPES[node.recipeId];
                                if (recipe) {
                                    recipe.ingredients.forEach(ingredient => {
                                        if (!assemblyItemFlow[ingredient.itemId]) {
                                            assemblyItemFlow[ingredient.itemId] = {consumed: 0, produced: 0, destinations: []};
                                        }
                                        assemblyItemFlow[ingredient.itemId].consumed += 1;
                                    });

                                    // Track production
                                    if (!assemblyItemFlow[recipe.result.itemId]) {
                                        assemblyItemFlow[recipe.result.itemId] = {consumed: 0, produced: 0, destinations: []};
                                    }
                                    assemblyItemFlow[recipe.result.itemId].produced += 1;
                                }
                            }
                        }
                    });

                    // Track item flow through connections to see where items go
                    setup.connections.forEach(conn => {
                        const fromNode = setup.nodes.find(n => n.id === conn.fromId);
                        const toNode = setup.nodes.find(n => n.id === conn.toId);
                        if (fromNode && toNode) {
                            if (!assemblyItemFlow[conn.itemId]) {
                                assemblyItemFlow[conn.itemId] = {consumed: 0, produced: 0, destinations: []};
                            }
                            const destination = toNode.type === "assembler"
                                ? `Assembler (${toNode.label})`
                                : toNode.type === "inventory"
                                ? "Inventory"
                                : "Other";
                            if (!assemblyItemFlow[conn.itemId].destinations.includes(destination)) {
                                assemblyItemFlow[conn.itemId].destinations.push(destination);
                            }
                        }
                    });
                });
            }
        } catch (e) {
            console.warn("Failed to load assembly stats", e);
        }

        return {
            resources,
            basicMaterials,
            researchCount,
            miningStats,
            totalMinedPerSecond,
            smeltingStats,
            totalSmeltedPerSecond,
            totalCrafted,
            power: {
                current: inventory.powerStats.supply,
                max: inventory.powerStats.steamCapacity,
                demand: inventory.powerStats.demand,
            },
            assemblerCount,
            runningAssemblers,
            assemblyItemFlow,
            assemblerRecipes,
        };
    }, [inventory]);
}
